import { useFormContext } from "react-hook-form"
import { classes } from "../../../../../util/style-utils";

/**
 * Lines of Business Section Component
 * 
 * A form section that allows users to select current lines of business being sold.
 * This component provides checkboxes for different insurance types and an optional
 * text input for specifying other lines of business when "Other" is selected.
 * 
 * @component
 * @returns {JSX.Element} The rendered lines of business selection form section
 * 
 * @example
 * // Usage within a react-hook-form FormProvider context
 * <FormProvider methods={methods}>
 *   <LinesOfBusinessSection />
 * </FormProvider>
 * 
 * @requires react-hook-form - Must be used within a FormProvider context
 * 
 * Form fields:
 * - lobPersonal: Boolean checkbox for Personal Auto
 * - lobCommercial: Boolean checkbox for Commercial insurance
 * - lobHome: Boolean checkbox for Home insurance
 * - lobHealth: Boolean checkbox for Health insurance
 * - lobOther: Boolean checkbox for Other insurance types
 * - otherLineOfBusiness: String input for specifying other insurance types (conditional)
 * 
 * Validation errors handled:
 * - soldLinesOfBusiness: Error for the checkbox group selection
 * - otherLineOfBusiness: Error for the "other" specification text input
 */
export default function LinesOfBusinessSection() {
    const { formState: { errors }, register, watch } = useFormContext();

    const watchLobOther = watch("lobOther");

    return <>
        <fieldset className={classes("form-field checkbox-group", errors.soldLinesOfBusiness && "form-field--error")}>
            <legend>
                Current lines of business being sold{/*
                */}<span className='required'>*</span>
            </legend>
            <span className='form-descriptive-copy'>Select all options that apply.</span>
            <div className="checkbox-wrapper col-2">
                <div className="label--description">
                    <input data-onboarding-checkbox-id="shared:PersonalInformation:LinesOfBusinessSection:Personal" id="lob-checkbox-ppa" type="checkbox" {...register("lobPersonal")} />
                    <label htmlFor="lob-checkbox-ppa" className="checkbox"><span>Personal Auto</span></label>
                </div>
                <div className="label--description">
                    <input data-onboarding-checkbox-id="shared:PersonalInformation:LinesOfBusinessSection:Commercial" id="lob-checkbox-commercial" type="checkbox" {...register("lobCommercial")} />
                    <label htmlFor="lob-checkbox-commercial" className="checkbox"><span>Commercial</span></label>
                </div>
                <div className="label--description">
                    <input data-onboarding-checkbox-id="shared:PersonalInformation:LinesOfBusinessSection:Home" id="lob-checkbox-home" type="checkbox" {...register("lobHome")} />
                    <label htmlFor="lob-checkbox-home" className="checkbox"><span>Home</span></label>
                </div>
                <div className="label--description">
                    <input data-onboarding-checkbox-id="shared:PersonalInformation:LinesOfBusinessSection:Health" id="lob-checkbox-health" type="checkbox" {...register("lobHealth")} />
                    <label htmlFor="lob-checkbox-health" className="checkbox"><span>Health</span></label>
                </div>
                <div className="label--description">
                    <input data-onboarding-checkbox-id="shared:PersonalInformation:LinesOfBusinessSection:Other" id="lob-checkbox-other" type="checkbox" {...register("lobOther")} />
                    <label htmlFor="lob-checkbox-other" className="checkbox"><span>Other</span></label>
                </div>
                {errors.soldLinesOfBusiness &&
                    <span className="form-message">{errors.soldLinesOfBusiness.message}</span>
                }
            </div>
        </fieldset>

        {watchLobOther &&
            <div className={classes("form-field", errors.otherLineOfBusiness && "form-field--error")}>
                <label htmlFor="other-line-of-business" className="text">
                    Please Specify Other Lines of Business{/*
                    */}<span className='required'>*</span>
                </label>
                <input id="other-line-of-business" type="text" {...register("otherLineOfBusiness")} />
                {errors.otherLineOfBusiness &&
                    <span className="form-message">{errors.otherLineOfBusiness.message}</span>
                }
            </div>
        }
    </>;
}